import React, { useEffect, useState, useMemo, useCallback } from "react";
import styles from "./HomeProductCard.module.scss";
import HeartIcon from "../../assets/images/product-section/icon/heart.svg";
import placeholderImage from "../../assets/images/placeholder.jpg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import HomeServices from "../../pages/home/homeService/HomeService";
import ReactLoading from "react-loading";
import { fetchFavorites } from "../../redux/Slices/FavoriteSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { fetchBasketData } from "../../redux/Slices/BasketSlice";
import BasketServices from "../basket/basketService/BasketService";
import { Modal } from "antd";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import InfoModalComponent from "../modalMessage/InfoModalMessage";
import ShoppingIcon from "../icon/ShoppingIcon";
import { getCurrencySymbol } from "../../helper/currencyHelper";

interface HomeProductCardProps {
  image: string;
  title: string;
  price: number | string;
  discount_price?: number | string | null;
  original_price?: number | string | null;
  promotion_price?: number | string | null;
  discount?: string;
  currency?: string;
  channel?: string;
  buttonText: string;
  onButtonClick?: () => void;
  customStyle?: React.CSSProperties;
  discountPosition?: string;
  productId: string;
  discount_percentage?: number;
  showDiscountBadge?: boolean;
  wishlistStatus?: boolean;
  isNew?: boolean;
  slug: string;
}


const HomeProductCard: React.FC<HomeProductCardProps> = ({
  image,
  title,
  discount_percentage,
  showDiscountBadge = true,
  price,
  discount_price,
  original_price,
  promotion_price,
  discount,
  currency,
  channel,
  buttonText,
  onButtonClick,
  customStyle,
  discountPosition,
  productId,
  wishlistStatus = false,
  isNew = false,
  slug,
}) => {
  const [loading, setLoading] = useState(false);
  const [wishlistStatusState, setWishlistStatusState] =
    useState<boolean>(wishlistStatus);
  const [loadingBasketAdd, setLoadingBasketAdd] = useState(false);
  const [isAddedToBasket, setIsAddedToBasket] = useState(false);
  useEffect(() => {
    setWishlistStatusState(wishlistStatus);
  }, [wishlistStatus]);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useSelector((state: RootState) => state.theme.mode);
  const dispatch = useDispatch<AppDispatch>();

  const targetDetailState = useMemo(() => {
    const currentPath = `${location.pathname}${location.search}`;
    const isSearch = Boolean(
      location.search.includes("search=") ||
      location.search.includes("search_type=") ||
      location.search.includes("keyword=")
    );
    return {
      from: currentPath,
      fromSearch: isSearch,
    };
  }, [location.pathname, location.search]);

  const translations = useSelector(
    (state: RootState) => state.translations.data
  );
  const selectedLanguage = useSelector(
    (state: RootState) => state.language.selectedLanguage?.code
  );

  const showErrorModal = useCallback((message: string) => {
    Modal.error({
      title: translations?.error,
      content: message,
      onOk() {},
    });
  }, [translations]);

  const detailButtonText = useMemo(() => {
    if (selectedLanguage === "ru") {
      return translations?.detailed || translations?.details || "Подробнее";
    }
    if (selectedLanguage === "en") {
      return translations?.detailed || translations?.details || "Details";
    }
    return "Ətraflı";
  }, [selectedLanguage, translations]);

  const handleAddToWishlist = useCallback(async (id: number, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const token = localStorage.getItem("token");
    if (!token) {
      const returnPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("returnUrl", returnPath);
      navigate(`/login?redirect=${encodeURIComponent(returnPath)}`);
      return;
    }

    setLoading(true);

    try {
      const response = await HomeServices.addProductToWishlist({
        product_id: id,
      });
      if (response.status) {
        setWishlistStatusState(response.add_type);
        dispatch(fetchFavorites());
      } else {
        setWishlistStatusState(response.add_type);
      }
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        const returnPath = window.location.pathname + window.location.search;
        sessionStorage.setItem("returnUrl", returnPath);
        navigate(`/login?redirect=${encodeURIComponent(returnPath)}`);
        return;
      }
      let errorMsg = translations?.error || "Bir xəta baş verdi";
      if (error instanceof AxiosError) {
        const errData = error.response?.data;
        if (errData?.errors) {
          const firstKey = Object.keys(errData.errors)[0];
          if (firstKey && Array.isArray(errData.errors[firstKey]) && errData.errors[firstKey].length > 0) {
            errorMsg = errData.errors[firstKey][0];
          }
        } else if (errData?.message) {
          errorMsg = errData.message;
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [dispatch, navigate, translations]);

  const handleAddBasket = useCallback(async (id: number) => {
    const token = localStorage.getItem("token");
    if (!token) {
      const returnPath = window.location.pathname + window.location.search;
      sessionStorage.setItem("returnUrl", returnPath);
      navigate(`/login?redirect=${encodeURIComponent(returnPath)}`);
      return;
    }

    setLoadingBasketAdd(true);
    try {
      await BasketServices.AddBasket({
        product_id: id,
        quantity: 1,
      });
      dispatch(fetchBasketData());
      setIsAddedToBasket(true);
      toast.success(translations?.added_to_cart || "Məhsul səbətə əlavə edildi");
      setTimeout(() => {
        setIsAddedToBasket(false);
      }, 2500);
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 401) {
        const returnPath = window.location.pathname + window.location.search;
        sessionStorage.setItem("returnUrl", returnPath);
        navigate(`/login?redirect=${encodeURIComponent(returnPath)}`);
        return;
      }
      let errorMsg = translations?.error || "Bir xəta baş verdi";
      if (error instanceof AxiosError) {
        const errData = error.response?.data;
        if (errData?.errors) {
          const firstKey = Object.keys(errData.errors)[0];
          if (firstKey && Array.isArray(errData.errors[firstKey]) && errData.errors[firstKey].length > 0) {
            errorMsg = errData.errors[firstKey][0];
          }
        } else if (errData?.message) {
          errorMsg = errData.message;
        }
      }
      toast.error(errorMsg);
    } finally {
      setLoadingBasketAdd(false);
    }
  }, [dispatch, navigate, translations]);

  const parseNumericValue = (val: number | string | null | undefined): number => {
    if (!val) return 0;
    const normalized = String(val).replace(/,/g, "").trim();
    const num = Number(normalized);
    return isNaN(num) ? 0 : num;
  };

  const currencySymbol = useMemo(() => getCurrencySymbol(currency), [currency]);

  const activePrice = useMemo(() => {
    if (parseNumericValue(discount_price) > 0) {
      return discount_price;
    }
    if (parseNumericValue(promotion_price) > 0) {
      return promotion_price;
    }
    return price ?? "0";
  }, [discount_price, promotion_price, price]);

  const strikePrice = useMemo(() => {
    if (parseNumericValue(original_price) > 0) {
      return original_price;
    }
    return price ?? "0";
  }, [original_price, price]);

  const hasDiscount =
    parseNumericValue(discount_price) > 0 ||
    parseNumericValue(promotion_price) > 0;

  return (
    <div
      style={customStyle}
      className={`${styles.homeProductCard} ${theme === "dark" ? styles.homeProductCard_dark : ""}`}
    >
      <Link
        to={`/productDetail/${slug}`}
        state={targetDetailState}
        key={productId}
        className={styles.cardImageLink}
      >
        <div className={styles.homeProductCardImgContainer}>
          <img
            src={image || placeholderImage}
            alt={title || "Məhsul"}
            className={styles.homeProductCardImg}
            width={300}
            height={220}
            loading="lazy"
            decoding="async"
            onError={(e) => {
              (e.target as HTMLImageElement).src = placeholderImage;
            }}
          />

          <div className={styles.cardBadges}>
            {channel && (
              <div className={styles.productCardChannelBadge} title={channel}>
                {channel}
              </div>
            )}
            {showDiscountBadge &&
              discount_percentage !== undefined &&
              discount_percentage > 0 && (
                <div className={styles.productCardHeaderDiscount}>
                  -{discount_percentage}%
                </div>
              )}
          </div>

          <div
            className={`${styles.homeProductCardImgIcon} ${wishlistStatusState ? styles.wishlistActive : ""}`}
            onClick={(e) => handleAddToWishlist(Number(productId), e)}
            role="button"
            tabIndex={0}
            aria-label={translations?.my_wishlist || "Seçilmişlər"}
          >
            {loading ? (
              <div className={styles.loadingHeartIcon}>
                <ReactLoading
                  type={"spin"}
                  color={wishlistStatusState ? "#fff" : "#344733"}
                  height={18}
                  width={18}
                />
              </div>
            ) : wishlistStatusState ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
            ) : (
              <img src={HeartIcon} alt="Heart Icon" width={18} height={18} />
            )}
          </div>
        </div>
      </Link>

      <div className={styles.cardContent}>
       

        <h3 className={styles.productTitle} title={title}>
          <Link to={`/productDetail/${slug}`} state={targetDetailState}>
            {title}
          </Link>
        </h3>

        <div className={styles.priceContainer}>
          {hasDiscount && parseNumericValue(strikePrice) > parseNumericValue(activePrice) ? (
            <>
              <span className={styles.price}>
                {activePrice} {currencySymbol}
              </span>
              <span className={styles.oldPrice}>
                {strikePrice} {currencySymbol}
              </span>
            </>
          ) : (
            <span className={styles.price}>
              {activePrice} {currencySymbol}
            </span>
          )}
        </div>

        <div className={styles.cardActions}>
          <Link
            to={`/productDetail/${slug}`}
            state={targetDetailState}
            className={styles.detailBtn}
            aria-label={detailButtonText}
            tabIndex={0}
          >
            {detailButtonText}
          </Link>

          {/* {loadingBasketAdd ? (
            <div className={styles.basketLoadingWrapper}>
              <ReactLoading
                type={"spin"}
                color={"#344733"}
                height={20}
                width={20}
              />
            </div>
          ) : (
            <button
              className={`${styles.cartBtn} ${isAddedToBasket ? styles.cartBtnAdded : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onButtonClick) {
                  onButtonClick();
                  setIsAddedToBasket(true);
                  setTimeout(() => {
                    setIsAddedToBasket(false);
                  }, 2500);
                  return;
                }
                handleAddBasket(Number(productId));
              }}
              aria-label={isAddedToBasket ? "Səbətə əlavə olundu" : (translations?.add_to_basket || "Səbət")}
              title={isAddedToBasket ? "Səbətə əlavə olundu" : (translations?.add_to_basket || "Səbət")}
              tabIndex={0}
            >
              {isAddedToBasket ? (
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <ShoppingIcon />
              )}
            </button>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default React.memo(HomeProductCard);
